"use client";

import React, { useEffect, useRef, useState } from "react";
import { Users, Target, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Personalized Attention",
  description: "We provide dedicated support tailored to the specific nuances of your medical or legal requirements, ensuring every interaction is handled with care.",
  icon: Users,
  number: "01",
  pulse: true
}, {
  title: "Deep Specialization",
  description: "Our interpreters are not just linguists; they are subject matter experts in healthcare and law, maintaining the highest levels of terminology accuracy.",
  icon: Target,
  number: "02",
  pulse: false
}, {
  title: "Technical Stability",
  description: "Built on a foundation of enterprise-grade hardware and redundant wired connections to guarantee zero-fail communication when it matters most.",
  icon: Zap,
  number: "03",
  pulse: false
}];
export default function WhyChooseUs() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223817" id="the-glyphic-standard" className="bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 border-t border-muted">
      <div ref={sectionRef} className={`mx-auto max-w-7xl transition-all duration-1000 transform ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4 tracking-tight">
            The GLYPHIC Standard
          </h2>
          <p className="text-lg font-default text-muted-foreground max-w-2xl mx-auto">
            High-performance technical precision for medical and legal remote interpretation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <div data-index={i} key={i} className="h-full">
                <Card className="h-full bg-[var(--card-warm)] border-border rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group relative overflow-hidden">
                  {}
                  <span className="absolute -bottom-4 -right-2 text-8xl font-serif font-bold text-foreground/5 select-none">
                    {feature.number}
                  </span>
                  
                  <CardHeader className="pb-4 pt-8 px-8 relative z-10">
                    <div className="relative inline-flex mb-6 transition-transform duration-300 group-hover:scale-105">
                      <div className={`p-4 bg-primary/5 rounded-none text-primary ${feature.pulse ? "animate-pulse" : ""}`}>
                        <Icon className="w-8 h-8" strokeWidth={1.5} />
                      </div>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-chart-2 rounded-full border-2 border-card shadow-sm"></div>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground tracking-tight mb-2">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-8 pb-8 relative z-10">
                    <p className="text-muted-foreground leading-loose font-default tracking-normal">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}